import { Logger } from '@/lib/Logging';

import { APIPath, getLinks, getPage, PageResponse, paths } from '@/cms';
import { EntryVersion } from '@/cms/types/entry';
import { env } from '@/constant/process';
import { ApiCallStrapiProps } from '@/utils/fetch';

export const homeSlug = 'home';

const logger = new Logger('utils.ts');

export async function fetchPageDataForAny(params: {
  slug: string[];
  status?: EntryVersion;
  fetchOptions?: ApiCallStrapiProps['fetchOptions'];
}) {
  const urlPath = params.slug;

  let apiPath: APIPath = '/pages';
  let slug: string = urlPath[0];

  const fullSlug = urlPath.join('/');

  const currentPath = paths.find((path) => {
    const match =
      path.urlPathStartsWith && fullSlug.startsWith(path.urlPathStartsWith);
    return match || path?.urlPathStartsWith === slug || path.urlPrefix === slug;
  });
  if (urlPath.length === 1 && currentPath) {
    apiPath = currentPath.apiPath;
    slug = '/';
  }
  if (urlPath.length > 1) {
    if (currentPath) {
      // TODO: a bit forced way to get the apiPath - will explain at some point and fix it
      // TODO blog (url) => blogs (endpoint url), proxies (url) => proxies (endpoint url)
      apiPath = currentPath.apiPath;
      slug = fullSlug.replace(
        currentPath.urlPathStartsWith
          ? `${currentPath.urlPathStartsWith}/`
          : '',
        ''
      );
    } else {
      slug = urlPath.join('/');
    }
  }

  const { data } = await getPage({
    slug,
    apiPath,
    status: params.status,
    fetchOptions: params.fetchOptions,
  });

  return data;
}

export const getSplitSlugFromLink = (link: PageResponse['data'][number]) => {
  const slug = link.attributes.slug;

  let splitSlug: string[];

  if (slug === '/') {
    splitSlug = link.attributes.prefixSlug ? [link.attributes.prefixSlug] : [];
  } else {
    splitSlug = slug.split('/').filter((segment) => segment !== '');
  }

  return splitSlug;
};

export async function generateStaticParamsHelper({
  apiPath,
}: { apiPath?: APIPath } = {}): Promise<{ slug: string[] }[]> {
  // Isolated builds skip pre-rendering: build-time SSG can only reach the
  // shared stage CMS, while on-demand rendering in the pod uses the branch's
  // isolated CMS - so rendering lazily is both faster and more faithful.
  if (env.isIsolatedBuild) {
    return [];
  }

  const links = await getLinks({ apiPath });
  const paths: { slug: string[] }[] = [];

  if (!links) {
    logger.error({
      message:
        'No links were found in generateStaticParams before links were formatted',
      context: JSON.stringify(links),
    });
    return paths;
  }

  links.forEach((link) => {
    if (
      !link ||
      link.attributes.originalSlug === homeSlug ||
      link.attributes.originalSlug === null
    ) {
      return;
    }

    const splitSlug = getSplitSlugFromLink(link);

    paths.push({ slug: splitSlug });
  });

  if (paths.length === 0) {
    logger.error({
      message:
        'No paths were found in generateStaticParams after links were formatted',
      context: {
        paths,
        links,
      },
    });
  }

  return paths;
}

/**
 * @param relations Context:
 *    https://datatroops.atlassian.net/browse/AWD-5271
 *    https://github.com/strapi/strapi/issues/23858
 *
 * This will be removed once this issue is fixed
 */
export function __hack__strapiRelationsOrderReverse<T>(relations: T[]): T[] {
  return relations.toReversed();
}
export function createAllowedStrapiOrigins(urls: string[]) {
  const origins = new Set<string>();

  for (const url of [env.publicStrapiAdminUrl, env.publicStrapiApiUrl, ...urls]) {
    if (!url) continue;
    try {
      const parsed = new URL(url);
      origins.add(parsed.origin);
      origins.add(parsed.origin.replace('127.0.0.1', 'localhost'));
      origins.add(parsed.origin.replace('localhost', '127.0.0.1'));
    } catch {
      // not a URL, add as-is (e.g. "'self'")
      origins.add(url);
    }
  }

  return origins;
}
